/* Mojis PDP cart adapter. A successful response must belong to this exact submission. */
(() => {
  const error = (code, message) => Object.assign(new Error(message), { code });
  const positiveInteger = value => /^(?:[1-9]\d*)$/.test(String(value)) && Number.isSafeInteger(Number(value));

  function prepare({ variant, variants, formData }) {
    const id = String(formData.get('id') || '');
    const quantity = String(formData.get('quantity') || '');
    if (!variant || !variant.available || String(variant.id) !== id || !variants.some(item => String(item.id) === id && item.available)) {
      throw error('INVALID_VARIANT', 'Please choose an available option.');
    }
    if (!positiveInteger(quantity)) throw error('INVALID_QUANTITY', 'Enter a valid quantity.');
    const rule = variant.quantity_rule || {};
    const min = Number.isSafeInteger(rule.min) && rule.min > 0 ? rule.min : 1;
    const max = Number.isSafeInteger(rule.max) && rule.max > 0 ? rule.max : Number.MAX_SAFE_INTEGER;
    const increment = Number.isSafeInteger(rule.increment) && rule.increment > 0 ? rule.increment : 1;
    if (Number(quantity) < min || Number(quantity) > max || (Number(quantity) - min) % increment !== 0) {
      throw error('INVALID_QUANTITY', 'Quantity does not meet this option’s purchase rules.');
    }
    return { id, quantity: Number(quantity), formData };
  }

  function checkoutUrl(root, origin) {
    if (typeof root !== 'string' || !/^\/(?!\/)(?:[A-Za-z0-9_-]+\/)?$/.test(root)) throw error('INVALID_ROUTE', 'Checkout route unavailable.');
    const base = new URL(root, origin);
    if (base.origin !== origin) throw error('INVALID_ROUTE', 'Checkout route unavailable.');
    return new URL('checkout', base).href;
  }

  async function submit({ url, prepared, fetchImpl = fetch, signal, timeoutMs = 15000 }) {
    if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) throw error('INVALID_ROUTE', 'Cart route unavailable.');
    const controller = new AbortController();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener('abort', () => controller.abort(), { once: true });
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response;
    try {
      response = await fetchImpl(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: prepared.formData,
        signal: controller.signal,
      });
    } catch (cause) {
      clearTimeout(timer);
      throw error('UNCERTAIN_CART_RESULT', 'Cart response was interrupted. Check your cart before trying again.');
    }
    let payload;
    try { payload = await response.json(); } catch (_) { clearTimeout(timer); throw error('INVALID_RESPONSE', 'Cart response was invalid. Check your cart before trying again.'); }
    clearTimeout(timer);
    if (!response.ok || payload.status) throw error('CART_REJECTED', payload.description || payload.message || 'Unable to add this option.');
    const items = Array.isArray(payload.items) ? payload.items : [payload];
    const expectedProperties = [...prepared.formData.entries()]
      .filter(([name]) => name.startsWith('properties[') && name.endsWith(']'))
      .map(([name, value]) => [name.slice(11, -1), String(value)]);
    const expectedPlan = String(prepared.formData.get('selling_plan') || '');
    if (!items.some(item => {
      if (String(item.id) !== prepared.id || !Number.isSafeInteger(Number(item.quantity)) || Number(item.quantity) < prepared.quantity) return false;
      const actualProperties = Object.entries(item.properties || {}).map(([name, value]) => [name, String(value)]);
      if (actualProperties.length !== expectedProperties.length || !expectedProperties.every(([name, value]) => actualProperties.some(([actualName, actualValue]) => actualName === name && actualValue === value))) return false;
      if (String(item.selling_plan_allocation?.selling_plan?.id || '') !== expectedPlan) return false;
      return true;
    })) {
      throw error('MISMATCHED_RESPONSE', 'Cart response did not match your selection. Check your cart before trying again.');
    }
    return payload;
  }

  const api = Object.freeze({ prepare, submit, checkoutUrl });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.MojisPurchase = api;
})();
